import { useState, useEffect, useRef } from 'react';
import { getUserPreference, setUserPreference } from 'core/api/apiService';

/**
 * usePersistedFilters — wie useState für ein Filter-Objekt ({key: string[]}), aber die
 * AUSWAHL wird PRO USER gemerkt (user_preferences, debounced gespeichert). Gegenstück zu
 * useVisibleFilters (das merkt nur, WELCHE Filter-Chips sichtbar sind — hier geht es um
 * die gewählten WERTE, damit der Nutzer seine Filter nicht bei jedem Besuch neu setzt).
 *
 * @param {Object} user
 * @param {string} storageKey  pref-Key, z. B. 'banf-filter-values'
 * @param {Object} initial     Ausgangszustand; nur dessen Keys werden aus der Präferenz übernommen
 * @returns {[Object, (nextOrFn)=>void]}  Setter akzeptiert Objekt ODER Updater-Funktion (wie setState)
 */
export default function usePersistedFilters(user, storageKey, initial) {
  const [filters, setFilters] = useState(initial);
  const loaded = useRef(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const pref = await getUserPreference(user, storageKey);
        if (alive && pref && pref.value && typeof pref.value === 'object') {
          // Nur bekannte Keys übernehmen (alte/entfernte Filter verwerfen), jeweils Arrays.
          setFilters((prev) => {
            const merged = { ...prev };
            Object.keys(initial).forEach((k) => {
              if (Array.isArray(pref.value[k])) merged[k] = pref.value[k];
            });
            return merged;
          });
        }
      } catch (_e) {
        // keine Präferenz / Fehler -> Default behalten
      } finally {
        loaded.current = true;
      }
    })();
    return () => {
      alive = false;
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const update = (nextOrFn) => {
    setFilters((prev) => {
      const next = typeof nextOrFn === 'function' ? nextOrFn(prev) : nextOrFn;
      if (loaded.current && user) {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
          setUserPreference(user, storageKey, next).catch(() => {});
        }, 600);
      }
      return next;
    });
  };

  return [filters, update];
}
